'use client';

import type { EntitlementsInfo, StripePromoCoupon, StripeStoreProduct } from '@inkitt/contracts-common';
import type { CountryCode } from '@inkitt/geo';
import { Star } from 'lucide-react';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { PointsContent } from './PointsContent';
import { SubscriptionCloseButton } from './SubscriptionCloseButton';
import { SubscriptionContent } from './SubscriptionContent';
import { TabSwitcher } from './TabSwitcher';

type SubscriptionLayoutProps = {
  subscriptions: StripeStoreProduct[];
  oneTimePackages: StripeStoreProduct[];
  appliedCoupon?: StripePromoCoupon;
  countryCode: CountryCode;
  activeTab: 'subscription' | 'points';
  onTabChange: (tab: 'subscription' | 'points') => void;
  storyId?: string;
  episodeNumber?: string;
  coupon?: string;
  source?: string;
  state?: string;
  trialDays?: number;
  entitlementsInfo: EntitlementsInfo;
  isAnonymous: boolean;
  onClearCoupon: () => void;
  onOpenPromoModal: () => void;
  pointsBalanceSlot: React.ReactNode;
};

export const SubscriptionLayout: React.FC<SubscriptionLayoutProps> = ({
  subscriptions,
  oneTimePackages,
  appliedCoupon,
  countryCode,
  activeTab,
  onTabChange,
  storyId,
  episodeNumber,
  coupon,
  state,
  trialDays,
  isAnonymous,
  onClearCoupon,
  onOpenPromoModal,
  pointsBalanceSlot,
}) => {
  const { t } = useTranslation();
  return (
    <div className="relative w-full mx-auto min-h-screen bg-transparent text-white">
      <SubscriptionCloseButton
        storyId={storyId}
        episodeNumber={episodeNumber}
        state={state}
        isAnonymous={isAnonymous}
      />

      <div className="pt-6 pb-3 mx-auto max-w-[500px] px-4">
        {/* Best Value Badge */}
        <div className="flex justify-center mb-3 w-1/2">
          <p className="flex items-center gap-3">
            <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
            <span className="text-yellow-500 text-sm font-bold">{t('subscription:best_value', 'Best Value')}</span>
          </p>
        </div>

        <TabSwitcher activeTab={activeTab} onTabChange={onTabChange} />

        {state === 'subscription_failure' && (
          <div className="mt-4 p-3 bg-red-900/30 rounded text-center">
            <p className="text-white text-sm">
              {t(
                'checkout:payment_could_not_be_processed',
                'Your payment could not be processed. Please try again or choose a different payment method.',
              )}
            </p>
          </div>
        )}

        {activeTab === 'subscription' ? (
          <SubscriptionContent
            subscriptions={subscriptions}
            countryCode={countryCode}
            storyId={storyId}
            episodeNumber={episodeNumber}
            coupon={coupon}
            appliedCoupon={appliedCoupon}
            onClearCoupon={onClearCoupon}
            onOpenPromoModal={onOpenPromoModal}
          />
        ) : (
          <PointsContent
            oneTimePackages={oneTimePackages}
            countryCode={countryCode}
            storyId={storyId}
            episodeNumber={episodeNumber}
            coupon={coupon}
            onOpenPromoModal={onOpenPromoModal}
          >
            <Suspense fallback={<div className="py-5 h-8 animate-pulse bg-neutral-700 rounded" />}>
              {pointsBalanceSlot}
            </Suspense>
          </PointsContent>
        )}
      </div>
    </div>
  );
};
